import React, { useState, useRef, useCallback } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import useCoupleAnalysis from '../hooks/useCoupleAnalysis'
import MSScoreSummary from '../components/admin/MSScoreSummary'
import ScoreComparisonCard, { ComparisonHeader } from '../components/admin/ScoreComparisonCard'
import { DCI_SUBSCALES, IPIP_DOMAINS, DOMAIN_FACETS, IPIP_FACETS } from '../lib/scoringConfig'
import InterpretationReport from '../components/interpretation/InterpretationReport'
import CoupleProfileSummary from '../components/CoupleProfileSummary'
import PdfExportMenu from '../components/PdfExportMenu'
import { generatePDF, generateCombinedPDF, buildPdfFilename } from '../lib/pdfExport'

function getBackPath(pathname) {
  if (pathname.startsWith('/facilitator')) return '/facilitator'
  if (pathname.startsWith('/client')) return '/client'
  return '/admin'
}

/* Extracted so it can be rendered both in the visible tab and the off-screen PDF container */
function QuantitativeScores({ person1, person2, msScores }) {
  const { t } = useTranslation(['report', 'scoring'])
  const p1Name = person1.respondent.name
  const p2Name = person2.respondent.name

  return (
    <div className="space-y-6">
      <CoupleProfileSummary person1={person1} person2={person2} />
      <MSScoreSummary person1={person1} person2={person2} msScores={msScores} />

      {/* DCI Section */}
      <section className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
        <h2 className="text-lg font-bold text-gray-800 mb-4">{t('report:quantitativeScores.dciTitle')}</h2>

        <ComparisonHeader person1Name={p1Name} person2Name={p2Name} />
        <ScoreComparisonCard
          label={t('report:quantitativeScores.totalDci')}
          person1Value={person1.dci.total}
          person2Value={person2.dci.total}
          person1Category={person1.dci.category}
          person2Category={person2.dci.category}
        />

        <div className="mt-4 pt-3 border-t border-gray-100">
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-2">{t('report:quantitativeScores.subscales')}</h3>
          <ComparisonHeader person1Name={p1Name} person2Name={p2Name} />
          {Object.entries(DCI_SUBSCALES).map(([key]) => (
            <ScoreComparisonCard
              key={key}
              label={`${key} \u2014 ${t(`scoring:dciSubscales.${key}`)}`}
              person1Value={person1.dci.subscales[key]?.total ?? '\u2014'}
              person2Value={person2.dci.subscales[key]?.total ?? '\u2014'}
            />
          ))}
        </div>
      </section>

      {/* IPIP Section */}
      <section className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
        <h2 className="text-lg font-bold text-gray-800 mb-4">{t('report:quantitativeScores.ipipTitle')}</h2>

        <ComparisonHeader person1Name={p1Name} person2Name={p2Name} />
        {Object.entries(IPIP_DOMAINS).map(([code]) => (
          <ScoreComparisonCard
            key={code}
            label={`${code} \u2014 ${t(`scoring:ipipDomains.${code}`)}`}
            person1Value={person1.ipip.domains[code]?.tScore != null ? Math.round(person1.ipip.domains[code].tScore) : '\u2014'}
            person2Value={person2.ipip.domains[code]?.tScore != null ? Math.round(person2.ipip.domains[code].tScore) : '\u2014'}
            person1Category={person1.ipip.domains[code]?.categoryLabel}
            person2Category={person2.ipip.domains[code]?.categoryLabel}
          />
        ))}

        {Object.entries(IPIP_DOMAINS).map(([domainCode]) => (
          <div key={domainCode} className="mt-6 pt-4 border-t border-gray-100">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-2">
              {t('report:quantitativeScores.facets', { code: domainCode, name: t(`scoring:ipipDomains.${domainCode}`) })}
            </h3>
            <ComparisonHeader person1Name={p1Name} person2Name={p2Name} />
            {DOMAIN_FACETS[domainCode].map(facetCode => {
              const p1Facet = person1.ipip.facets[facetCode]
              const p2Facet = person2.ipip.facets[facetCode]
              return (
                <ScoreComparisonCard
                  key={facetCode}
                  label={`${facetCode} \u2014 ${t(`scoring:ipipFacets.${facetCode}`)}`}
                  person1Value={p1Facet?.tScore != null ? Math.round(p1Facet.tScore) : '\u2014'}
                  person2Value={p2Facet?.tScore != null ? Math.round(p2Facet.tScore) : '\u2014'}
                  person1Category={p1Facet?.categoryLabel}
                  person2Category={p2Facet?.categoryLabel}
                />
              )
            })}
          </div>
        ))}
      </section>

      {/* IPIP Converted Scores */}
      <section className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
        <h2 className="text-lg font-bold text-gray-800 mb-4">{t('report:quantitativeScores.ipipConvertedTitle')}</h2>
        <ComparisonHeader person1Name={p1Name} person2Name={p2Name} />
        {Object.entries(IPIP_FACETS).map(([facetCode]) => {
          const p1Facet = person1.ipip.facets[facetCode]
          const p2Facet = person2.ipip.facets[facetCode]
          return (
            <ScoreComparisonCard
              key={facetCode}
              label={`${facetCode} \u2014 ${t(`scoring:ipipFacets.${facetCode}`)}`}
              person1Value={p1Facet?.converted ?? '\u2014'}
              person2Value={p2Facet?.converted ?? '\u2014'}
            />
          )
        })}
        <div className="mt-3 pt-3 border-t-2 border-gray-200">
          <ScoreComparisonCard
            label={t('report:quantitativeScores.totalConverted')}
            person1Value={person1.ipip.totalConverted}
            person2Value={person2.ipip.totalConverted}
          />
        </div>
      </section>
    </div>
  )
}

export default function CoupleAnalysis() {
  const { t } = useTranslation(['report', 'common'])
  const { id } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const backPath = getBackPath(location.pathname)
  const { person1, person2, msScores, loading, error } = useCoupleAnalysis(id)
  const [activeTab, setActiveTab] = useState('scores')
  const [exporting, setExporting] = useState(false)
  const [showOffscreen, setShowOffscreen] = useState(false)

  const offscreenScoresRef = useRef(null)
  const offscreenReportRef = useRef(null)

  const TABS = [
    { key: 'scores', label: t('report:coupleAnalysis.tabs.scores') },
    { key: 'report', label: t('report:coupleAnalysis.tabs.report') },
  ]

  const handleExport = useCallback(async (type) => {
    if (!person1 || !person2 || !msScores) return

    setExporting(true)
    setShowOffscreen(true)

    // Wait for off-screen content to render and layout
    await new Promise(r => setTimeout(r, 200))

    const p1Name = person1.respondent.name
    const p2Name = person2.respondent.name
    const title = `Couple Satisfaction Insight \u2014 ${p1Name} & ${p2Name}`

    try {
      if (type === 'scores') {
        await generatePDF(offscreenScoresRef.current, {
          title,
          filename: buildPdfFilename('quantitative', p1Name, p2Name),
        })
      } else if (type === 'report') {
        await generatePDF(offscreenReportRef.current, {
          title,
          filename: buildPdfFilename('qualitative', p1Name, p2Name),
        })
      } else {
        await generateCombinedPDF(offscreenScoresRef.current, offscreenReportRef.current, {
          title,
          filename: buildPdfFilename('couple-analysis', p1Name, p2Name),
        })
      }
    } catch (err) {
      console.error('PDF generation failed:', err)
    } finally {
      setExporting(false)
      setShowOffscreen(false)
    }
  }, [person1, person2, msScores])

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-gray-400 text-lg">{t('report:coupleAnalysis.loadingCoupleAnalysis')}</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="max-w-2xl mx-auto py-10 px-4">
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-6">
          <h3 className="font-semibold mb-1">{t('common:status.error')}</h3>
          <p className="text-sm">{error}</p>
        </div>
        <button
          onClick={() => navigate(backPath)}
          className="mt-4 text-primary-600 hover:text-primary-700 text-sm font-medium"
        >
          {t('report:coupleAnalysis.backToDashboard')}
        </button>
      </div>
    )
  }

  if (!person1 || !person2 || !msScores) return null

  const p1Name = person1.respondent.name
  const p2Name = person2.respondent.name

  return (
    <div className="max-w-4xl mx-auto py-6 px-4 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <button
            onClick={() => navigate(backPath)}
            className="text-primary-600 hover:text-primary-700 text-sm font-medium mb-2 inline-block"
          >
            {t('report:coupleAnalysis.backToDashboard')}
          </button>
          <h1 className="text-2xl font-bold text-gray-800">
            {t('report:coupleAnalysis.title', { person1: p1Name, person2: p2Name })}
          </h1>
        </div>
        <PdfExportMenu onExport={handleExport} exporting={exporting} />
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-1 border-b border-gray-200">
        {TABS.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.key
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'scores' && (
        <QuantitativeScores person1={person1} person2={person2} msScores={msScores} />
      )}

      {activeTab === 'report' && (
        <InterpretationReport person1={person1} person2={person2} msScores={msScores} />
      )}

      {/* Off-screen container for PDF capture */}
      {showOffscreen && (
        <div
          style={{ position: 'absolute', left: '-9999px', top: 0, width: '896px' }}
          className="pdf-capture bg-white"
          aria-hidden="true"
        >
          <div ref={offscreenScoresRef} className="p-6 pdf-capture">
            <QuantitativeScores person1={person1} person2={person2} msScores={msScores} />
          </div>
          <div ref={offscreenReportRef} className="p-6 pdf-capture">
            <InterpretationReport person1={person1} person2={person2} msScores={msScores} />
          </div>
        </div>
      )}
    </div>
  )
}
